import React from "react";
import { Box, Typography } from "@mui/joy";
import { RotateCw, FileDown, Eye } from "lucide-react";
import {
    DocumentPreviewWidgetV2,
    ContractPreview,
    type IMenuAction,
} from "../../";
import {
    useDocument,
    useAsset,
    useInspectionAttributes,
    useCart,
    useReport,
} from "../../../hooks/query";
import { useProjectQuery } from "../../../hooks/query";
import { useNotification } from "../../../hooks/useNotification";
import { useProject } from "../../../hooks";
import { useContractSigning } from "./hooks";
import { buildContractPreviewData } from "../../../utils/buildContractPreviewData";

export function ContractReview() {
    const { project } = useProject();
    const {
        contractDocument,
        workOrderDocument,
        purchaseOrderDocument,
        hasGutterWork,
        refreshContract,
        triggerSigning,
        isTriggeringSigning,
    } = useContractSigning();

    const {
        useGenerateContract,
        useGetContractPricing,
        useWorkOrderCreate,
        useSupplyOrderCreate,
    } = useDocument();
    const { useGeneratePreCannedReport } = useReport();
    const { useDownload } = useAsset();
    const { showSuccess, showError } = useNotification();

    const generateContractSvc = useGenerateContract();
    const getContractPricingSvc = useGetContractPricing();
    const generateWorkOrderSvc = useWorkOrderCreate();
    const generateSupplyOrderSvc = useSupplyOrderCreate();
    const generateGutterReportSvc = useGeneratePreCannedReport();
    const downloadAssetSvc = useDownload();

    const { useListByProjectId: useListAttributes } = useInspectionAttributes();
    const { useListByProjectId: useListCart } = useCart();
    const { useListById: useGetProject } = useProjectQuery();

    const listAttributesSvc = useListAttributes();
    const listCartSvc = useListCart();
    const getProjectSvc = useGetProject();

    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [previewData, setPreviewData] = React.useState<any>(null);

    const handleOpenPreview = async () => {
        if (!project?.id) return;
        try {
            const [attributes, projectResponse, contractPricing, cartItems] =
                await Promise.all([
                    listAttributesSvc.mutateAsync({ ProjectId: project.id }),
                    getProjectSvc.mutateAsync({ Id: project.id }),
                    getContractPricingSvc.mutateAsync({
                        ProjectId: project.id,
                    }),
                    listCartSvc.mutateAsync({ ProjectId: project.id }),
                ]);

            const fullProject = Array.isArray(projectResponse)
                ? projectResponse[0]
                : projectResponse;

            if (!fullProject) throw new Error("Project details not found");
            if (!contractPricing) throw new Error("Contract pricing not found");

            setPreviewData(
                buildContractPreviewData({
                    project: fullProject,
                    attributes,
                    contractPricing,
                    cartItems: Array.isArray(cartItems) ? cartItems : [],
                }),
            );
            setPreviewOpen(true);
        } catch (e) {
            console.error(e);
            showError("Failed to load project details for preview");
        }
    };

    const handleRegenerateContract = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateContractSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Contract regenerated successfully");
            setTimeout(() => {
                refreshContract();
            }, 1000);
        } catch (error) {
            console.error("Error regenerating contract:", error);
            showError("Failed to regenerate contract");
        }
    };

    const handleSendForSigning = async () => {
        try {
            await triggerSigning();
            showSuccess("Contract sent for signing via BoldSign successfully");
            setPreviewOpen(false);
        } catch (error: any) {
            console.error("Error sending contract for signing:", error);
            showError(error.message || "Failed to send contract for signing");
        }
    };

    const handleGenerateWorkOrder = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateWorkOrderSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Work Order generated successfully");
            setTimeout(() => {
                refreshContract();
            }, 1000);
        } catch (error) {
            console.error("Error generating work order:", error);
            showError("Failed to generate work order");
        }
    };

    const handleGeneratePurchaseOrder = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateSupplyOrderSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Purchase Order generated successfully");
            setTimeout(() => {
                refreshContract();
            }, 1000);
        } catch (error) {
            console.error("Error generating purchase order:", error);
            showError("Failed to generate purchase order");
        }
    };

    const handleGenerateGutterReport = async () => {
        try {
            if (!project?.id) return;
            await generateGutterReportSvc.mutateAsync({
                ReportType: "GUTTER_REPORT",
                ProjectId: project.id,
            });
            showSuccess("Gutter Report downloaded successfully");
        } catch (error) {
            console.error("Error generating gutter report:", error);
            showError("Failed to generate gutter report");
        }
    };

    const handleDownloadContract = async () => {
        if (!contractDocument?.DocumentAssetId) {
            showError("Contract PDF not available yet");
            return;
        }
        try {
            await downloadAssetSvc.mutateAsync({
                AssetId: contractDocument.DocumentAssetId,
            });
            showSuccess("Contract downloaded successfully");
        } catch (error) {
            console.error("Error downloading contract:", error);
            showError("Failed to download contract");
        }
    };

    const contractStatus = contractDocument?.Status;
    const contractMenuActions: IMenuAction[] = contractDocument
        ? [
              ...(contractDocument.DocumentAssetId
                  ? [
                        {
                            label: "Download",
                            icon: <FileDown size={16} />,
                            onClick: handleDownloadContract,
                            disabled: downloadAssetSvc.isPending,
                        },
                    ]
                  : []),
              {
                  label: "Preview",
                  icon: <Eye size={16} />,
                  onClick: handleOpenPreview,
              },
              {
                  label: "Regenerate",
                  icon: <RotateCw size={16} />,
                  onClick: handleRegenerateContract,
                  disabled: generateContractSvc.isPending,
              },
          ]
        : [];

    const workOrderMenuActions: IMenuAction[] = workOrderDocument
        ? [
              {
                  label: "Regenerate",
                  icon: <RotateCw size={16} />,
                  onClick: handleGenerateWorkOrder,
                  disabled: generateWorkOrderSvc.isPending,
              },
          ]
        : [];

    const purchaseOrderMenuActions: IMenuAction[] = purchaseOrderDocument
        ? [
              {
                  label: "Regenerate",
                  icon: <RotateCw size={16} />,
                  onClick: handleGeneratePurchaseOrder,
                  disabled: generateSupplyOrderSvc.isPending,
              },
          ]
        : [];

    return (
        <Box mt={4} display="flex" flexDirection="column" gap={4}>
            {/* Contract Section */}
            <Box>
                <Typography
                    level={"body-sm"}
                    sx={{ color: "text.primary", fontWeight: 600 }}
                    gutterBottom
                >
                    Documents Review
                </Typography>
                <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                    Formal agreement to define project deliverables, timelines,
                    and obligations.
                </Typography>
                <Box
                    mt={2}
                    sx={{
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(auto-fill, minmax(280px, 1fr))",
                        },
                        gap: 2.5,
                    }}
                >
                    <DocumentPreviewWidgetV2
                        title="Contract.pdf"
                        description={
                            contractStatus
                                ? `Project agreement and terms · ${contractStatus}`
                                : "Project agreement and terms"
                        }
                        fileType="PDF"
                        assetId={contractDocument?.DocumentAssetId}
                        onButtonClick={handleOpenPreview}
                        loading={
                            generateContractSvc.isPending ||
                            listAttributesSvc.isPending ||
                            getProjectSvc.isPending ||
                            downloadAssetSvc.isPending
                        }
                        menuActions={contractMenuActions}
                    />
                    <DocumentPreviewWidgetV2
                        title="Work Order.pdf"
                        description={
                            workOrderDocument
                                ? `Scope and materials for the crew · ${workOrderDocument.Status}`
                                : "Click to generate work order"
                        }
                        fileType="PDF"
                        assetId={workOrderDocument?.DocumentAssetId}
                        onButtonClick={
                            workOrderDocument
                                ? undefined
                                : handleGenerateWorkOrder
                        }
                        loading={generateWorkOrderSvc.isPending}
                        menuActions={workOrderMenuActions}
                    />
                    <DocumentPreviewWidgetV2
                        title="Purchase Order.pdf"
                        description={
                            purchaseOrderDocument
                                ? `Material supply order request · ${purchaseOrderDocument.Status}`
                                : "Click to generate purchase order"
                        }
                        fileType="PDF"
                        assetId={purchaseOrderDocument?.DocumentAssetId}
                        onButtonClick={
                            purchaseOrderDocument
                                ? undefined
                                : handleGeneratePurchaseOrder
                        }
                        loading={generateSupplyOrderSvc.isPending}
                        menuActions={purchaseOrderMenuActions}
                    />
                    {hasGutterWork && (
                        <DocumentPreviewWidgetV2
                            title="Gutter Work Order.pdf"
                            description="Gutter work order and invoice report"
                            fileType="PDF"
                            onButtonClick={handleGenerateGutterReport}
                            loading={generateGutterReportSvc.isPending}
                        />
                    )}
                </Box>
            </Box>

            <ContractPreview
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                data={previewData}
                isGenerating={generateContractSvc.isPending}
                onGenerate={async () => {
                    await handleRegenerateContract();
                    setPreviewOpen(false);
                }}
                onSend={handleSendForSigning}
                isSending={isTriggeringSigning}
                isSendDisabled={
                    !contractDocument || contractDocument?.Status === "INIT"
                }
                onDownload={handleDownloadContract}
                isDownloading={downloadAssetSvc.isPending}
                isDownloadDisabled={!contractDocument?.DocumentAssetId}
            />
        </Box>
    );
}
